import React, { useContext, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { suggestRestaurant } from "../../apollo/server";
import UserContext from "../../context/User";
import { useLocationContext } from "../../context/Location";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, TYPE } from "../../utils/designTokens";
import { fieldSx, labelSx } from "../Checkout/checkoutUi";
import RmfDialog, { DialogActions, DialogIcon, DialogText, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";

const SUGGEST = gql`${suggestRestaurant}`;

/**
 * The "Suggest a restaurant" popup (signed-in customers; the backend keeps 20
 * a day each). `intro` is the line under the title; the Catering page and the
 * no-delivery view (components/PickupNearby) each ask in their own words.
 */
export function SuggestRestaurantDialog({ open, onClose, city, intro, onMessage }) {
  const { isLoggedIn } = useContext(UserContext);
  const { location } = useLocationContext();
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [send, { loading }] = useMutation(SUGGEST);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await send({
        variables: {
          restaurantName: name.trim(),
          note: note.trim() || null,
          city: city || null,
          address: location?.deliveryAddress || null,
          latitude: location?.latitude != null ? Number(location.latitude) : null,
          longitude: location?.longitude != null ? Number(location.longitude) : null,
        },
      });
      onClose();
      setName("");
      setNote("");
      onMessage?.({ type: "success", message: "Thanks! We'll reach out to them." });
    } catch (err) {
      onMessage?.({ type: "error", message: err?.message || "Couldn't send that. Please try again." });
    }
  };

  return (
    <RmfDialog open={open} onClose={() => !loading && onClose()}>
      <DialogIcon tone="red" sx={{ mb: 2.5 }}>
        <RoomServiceRoundedIcon />
      </DialogIcon>
      <DialogTitle besideClose={false}>Suggest a restaurant</DialogTitle>
      {isLoggedIn ? (
        <Box component="form" onSubmit={submit}>
          <DialogText>{intro}</DialogText>
          <Box component="label" htmlFor="suggest-name" sx={{ ...labelSx, display: "block", mt: 2.5, mb: 0.75 }}>
            Restaurant name
          </Box>
          <Box component="input" id="suggest-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoComplete="off" sx={fieldSx} />
          <Box component="label" htmlFor="suggest-note" sx={{ ...labelSx, display: "block", mt: 2, mb: 0.75 }}>
            Anything we should know? (optional)
          </Box>
          <Box component="textarea" id="suggest-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} sx={{ ...fieldSx, height: "auto", py: 1.25, resize: "vertical" }} />
          <DialogActions>
            <Box component="button" type="submit" disabled={loading || !name.trim()} aria-busy={loading} sx={dialogButtonSx.primary}>
              {loading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Send suggestion"}
            </Box>
            <Box component="button" type="button" onClick={onClose} disabled={loading} sx={dialogButtonSx.secondary}>
              Cancel
            </Box>
          </DialogActions>
        </Box>
      ) : (
        <>
          <DialogText>Sign in so we can let you know when they join.</DialogText>
          <DialogActions>
            <Box component={RouterLink} to="/login" sx={dialogButtonSx.primary}>
              Sign in
            </Box>
            <Box component="button" type="button" onClick={onClose} sx={dialogButtonSx.secondary}>
              Not now
            </Box>
          </DialogActions>
        </>
      )}
    </RmfDialog>
  );
}

/**
 * Under the caterers: "More caterers coming to {city}", and the suggestion
 * popup.
 */
function SuggestRestaurant({ city, onMessage }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          p: { xs: 2.5, sm: 3 },
          border: `1.5px dashed ${C.borderStrong}`,
          borderRadius: "20px",
        }}
      >
        <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: "16px", bgcolor: C.redTint, color: C.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="h3" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink }}>
            {city ? `More caterers coming to ${city}` : "More caterers coming soon"}
          </Box>
          <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.5, color: C.muted }}>
            Want a favourite local spot to cater your event? Tell us and we'll reach out to them.
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{ ...BUTTON_RESET, ...FOCUS_RING, flexShrink: 0, display: "inline-flex", alignItems: "center", minHeight: 44, px: 1, borderRadius: "10px", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: C.redLink, "&:hover": { textDecoration: "underline" } }}
        >
          Suggest a restaurant
          <ChevronRightRoundedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <SuggestRestaurantDialog
        open={open}
        onClose={() => setOpen(false)}
        city={city}
        intro="Which local spot should cater your events? We'll reach out to them."
        onMessage={onMessage}
      />
    </>
  );
}

export default SuggestRestaurant;
